import { LoadingIcon } from '@assets/icons';
import { Typography } from '@components/typography';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { Box, Stack, type BoxProps } from '@mui/material';
import { useEffect, useRef, useState, type ComponentType, type RefObject } from 'react';

const useScrollbarMargin = (ref: RefObject<HTMLElement> | undefined) => {
  const [hasScrollbar, setHasScrollbar] = useState(false);

  useEffect(() => {
    const el = ref?.current;
    if (!(el instanceof Element)) return undefined;
    const check = () => setHasScrollbar(el.scrollHeight > el.clientHeight);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => void observer.disconnect();
  }, [ref]);

  return hasScrollbar ? { mr: 2 } : {};
};

type InfiniteScrollListProps<
  K extends string,
  D extends Record<K, string> & Record<string, unknown>,
  E extends Record<string, unknown> = Record<string, never>,
> = {
  data: D[];
  loading: boolean;
  emptyText: string;
  fetchMore?: VoidFunction;
  containerProps?: BoxProps;
  itemContainerProps?: Omit<BoxProps, 'onClick'> & {
    onClick?: (data: D) => void;
  };
  highlightedId?: D[K];
  Item: ComponentType<{ data: D } & E>;
  itemProps?: E;
  itemKey: K;
};

export const InfiniteScrollList = <
  K extends string,
  D extends Record<K, string> & Record<string, unknown>,
  E extends Record<string, unknown> = Record<string, never>,
>({
  data,
  loading,
  emptyText,
  containerProps,
  itemContainerProps,
  highlightedId,
  Item,
  itemProps,
  itemKey,
  fetchMore,
}: InfiniteScrollListProps<K, D, E>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollbarMargin = useScrollbarMargin(containerRef);
  const sentinelRef = useInfiniteScroll({
    onLoadMore: fetchMore,
    loading,
    hasNextPage: Boolean(fetchMore),
  });

  return (
    <Stack
      ref={containerRef}
      sx={{
        gap: 2,
        width: '100%',
        minHeight: '300px',
        overflow: 'auto',
        flex: 1,
        containerType: 'inline-size',
      }}
    >
      {loading ? (
        <Stack sx={{ minHeight: '300px' }} alignItems="center" justifyContent="center">
          <LoadingIcon size={12} />
        </Stack>
      ) : data.length === 0 ? (
        <Stack sx={{ minHeight: '300px' }} alignItems="center" justifyContent="center">
          <Typography variant="body1" color="foreground.default.light">
            {emptyText}
          </Typography>
        </Stack>
      ) : (
        <Box
          {...containerProps}
          sx={{
            display: 'grid',
            gap: 2,
            columnGap: 3,
            ...containerProps?.sx,
          }}
        >
          {data.map((item) => (
            <Box
              key={item[itemKey]}
              role="button"
              {...itemContainerProps}
              onClick={() => itemContainerProps?.onClick?.(item)}
              sx={{
                ...scrollbarMargin,
                p: 2,
                display: 'grid',
                gridTemplateColumns: 'subgrid',
                backgroundColor: 'background.paper',
                alignItems: 'center',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                color: 'foreground.paper.default',
                '& .id-copy-button': {
                  visibility: 'hidden',
                },
                '&:hover': {
                  cursor: 'pointer',
                  '& .id-copy-button': {
                    visibility: 'visible',
                  },
                  borderColor: 'foreground.paperCard.light',
                },
                ...(highlightedId === item[itemKey] && {
                  backgroundColor: 'action.selected',
                  animation: 'highlight-pulse 1s ease-in-out 3',
                  '@keyframes highlight-pulse': {
                    '0%, 100%': {
                      backgroundColor: 'action.selected',
                      color: 'foreground.paper.default',
                    },
                    '50%': { backgroundColor: 'primary.light', color: 'primary.contrastText' },
                  },
                }),
                ...itemContainerProps?.sx,
              }}
            >
              <Item data={item} {...itemProps} />
            </Box>
          ))}
          <Box
            ref={sentinelRef}
            sx={{
              height: 1,
              gridColumn:
                containerProps?.sx &&
                typeof containerProps.sx === 'object' &&
                'gridColumn' in containerProps.sx &&
                typeof containerProps.sx.gridColumn === 'string'
                  ? containerProps.sx.gridColumn
                  : undefined,
            }}
          />
        </Box>
      )}
    </Stack>
  );
};
